import { memo, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export interface Card {
  id: string;
  balance: string;
  cardNumber: string;
  cardType: 'visa' | 'mastercard';
  type: string;
  network: string;
  network_logo_url: string;
  logo_url: string;
  creditLimit?: {
    amount: number;
    used: number;
  };
  gradient: readonly string[];
  isPremium?: boolean;
}

interface CardComponentProps {
  card: Card;
  index: number;
  faceCount: number;
  faceWidth: number;
  radius: number;
  isPremium: boolean;
  onClick: (card: Card, index: number) => void;
}

export const CardComponent = memo<CardComponentProps>(({ 
  card, 
  index, 
  faceCount, 
  faceWidth, 
  radius, 
  isPremium, 
  onClick 
}) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver({
    ref: cardRef,
    threshold: 0.1,
    enabled: !isPremium // Останавливаем анимации только для не-премиум карточек
  });

  const cardStyle = useMemo(() => ({
    width: `${faceWidth}px`,
    transform: `rotateY(${index * (360 / faceCount)}deg) translateZ(${radius}px)`,
    background: `linear-gradient(135deg, ${card.gradient.join(', ')})`
  }), [faceWidth, index, faceCount, radius, card.gradient]);

  return (
    <motion.div
      ref={cardRef}
      key={card.id}
      className={cn(
        'absolute text-white cursor-pointer',
        'rounded-3xl p-5',
        'transition-transform duration-[400ms] ease-out',
        'shadow-[0_4px_12px_rgba(0,0,0,0.2)]',
        'w-[280px] h-[220px] max-[640px]:w-[240px] max-[640px]:h-[180px] max-[640px]:p-4'
      )}
      style={{
        ...cardStyle,
        transformStyle: 'preserve-3d',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden'
      }}
      onClick={() => onClick(card, index)}
    >
      <div 
        className={cn(
          'h-full flex flex-col opacity-100 relative z-[1]'
        )}
        style={{
          transformStyle: 'preserve-3d',
          transform: 'translateZ(1px)',
          WebkitBackfaceVisibility: 'visible',
          backfaceVisibility: 'visible'
        }}
      >
        <div 
          className="absolute -inset-5 rounded-3xl -z-[1]"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%)'
          }}
        />
        
        <div className="flex flex-col" style={{ gap: '8px' }}>
          <div className="flex justify-between items-center w-full">
            <span className="text-sm font-medium m-0 opacity-90">APY</span>
            <img 
              src={card.network_logo_url} 
              alt={card.network} 
              className={cn(
                'w-6 h-6 rounded-full',
                !isPremium && 'blur-[5px] opacity-20 select-none pointer-events-none'
              )}
            />
          </div>
          <div className="w-full overflow-hidden">
            <h1 
              className="font-semibold m-0 tracking-[-0.5px] w-full whitespace-nowrap max-[640px]:text-[28px]"
              style={{
                fontSize: 'clamp(16px, 8vw, 32px)',
                transformOrigin: 'left'
              }}
            >
              {card.balance}
            </h1>
          </div>
        </div>

        <div className="w-full overflow-hidden">
          <div 
            className={cn(
              'font-semibold text-white text-left opacity-90',
              'flex items-center w-full whitespace-nowrap',
              'my-4 max-[640px]:my-3'
            )}
            style={{
              fontSize: 'clamp(12px, 4vw, 18px)',
              gap: '8px',
              transformOrigin: 'left'
            }}
          >
            <img src={card.logo_url} alt={card.type} className="w-6 h-6 rounded-full" />
            <span className={cn(!isPremium && 'blur-[5px] opacity-20 select-none pointer-events-none')}>
              {card.type}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-end mt-auto">
          <div 
            className={cn(
              'bg-white/15 rounded-xl flex flex-col',
              !isPremium && 'blur-[5px] opacity-20 select-none pointer-events-none',
              'max-[640px]:px-2.5 max-[640px]:py-1.5'
            )}
            style={{ 
              gap: '4px',
              padding: '8px 12px'
            }}
          >
            <span 
              className={cn(
                'text-xs font-medium text-white opacity-90',
                'max-[640px]:text-xs'
              )}
            >
              {card.cardNumber}
            </span>
          </div>
          {!isPremium && (
            <>
              <div 
                className="absolute bottom-[36%] right-1/2 translate-x-1/2 translate-y-1/2 translate-z-[2px] w-[42px] h-[42px] bg-white/25 rounded-full flex items-center justify-center text-white/90 shadow-[0_2px_8px_rgba(0,0,0,0.2)] z-[100]"
                style={{
                  transform: 'translate(50%, 50%) translateZ(2px)'
                }}
              >
                <div 
                  className="absolute -inset-0.5 rounded-full border-2 border-white/40"
                  style={{
                    animation: isIntersecting 
                      ? 'pulseWave 2.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite' 
                      : 'none',
                    boxShadow: '0 0 10px rgba(255, 255, 255, 0.3), inset 0 0 10px rgba(255, 255, 255, 0.2)'
                  }}
                />
                <div 
                  className="absolute -inset-0.5 rounded-full border-2 border-white/35"
                  style={{
                    animation: isIntersecting 
                      ? 'pulseWave 2.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite' 
                      : 'none',
                    animationDelay: '0.8s',
                    boxShadow: '0 0 15px rgba(255, 255, 255, 0.25), inset 0 0 8px rgba(255, 255, 255, 0.15)'
                  }}
                />
                <Lock size={20} />
              </div>
              <div 
                className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 translate-z-[2px] text-[10px] font-medium text-white/90 uppercase tracking-[0.5px] px-2 py-1 bg-white/15 rounded-md backdrop-blur-sm z-[100] whitespace-nowrap"
                style={{
                  transform: 'translateX(-50%) translateZ(2px)'
                }}
              >
                {t('HOME_SCREEN.PREMIUM_DEALS.GET_PREMIUM')}
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
});

CardComponent.displayName = 'Card';

